import React, { useState, useRef, useEffect } from 'react';
import { Table as TableIcon, Plus } from './icons';
import { createTable } from '@tiptap/extension-table';
import { TextSelection } from '@tiptap/pm/state';

/**
 * Universally safe table insertion helper:
 * - If inside an existing table, inserts the new table right after it with a trailing paragraph.
 * - If inside an empty paragraph, replaces it cleanly.
 * - Handles selection and focus restoration reliably.
 * - Falls back to direct ProseMirror transaction if TipTap insertTable returns false.
 */
export function safeInsertTable(editor, rows = 3, cols = 3, withHeaderRow = true) {
    if (!editor || editor.isDestroyed) return false;

    const rowCount = Math.max(1, Math.min(50, Number(rows) || 3));
    const colCount = Math.max(1, Math.min(25, Number(cols) || 3));

    const { state, view } = editor;
    const { schema, selection } = state;
    const $from = selection.$from;

    let insideTable = false;
    let tableEndPos = null;
    for (let d = $from.depth; d > 0; d--) {
        if ($from.node(d).type.name === 'table') {
            insideTable = true;
            tableEndPos = $from.after(d);
            break;
        }
    }

    const tableNode = createTable(schema, rowCount, colCount, withHeaderRow);
    const paragraphType = schema.nodes.paragraph;
    const trailingP = paragraphType ? paragraphType.create() : null;

    // 1. If currently inside an existing table, insert the new table right after it
    if (insideTable && tableEndPos !== null) {
        const tr = state.tr;
        const nodesToInsert = trailingP ? [tableNode, trailingP] : [tableNode];
        tr.insert(tableEndPos, nodesToInsert);
        const firstCellPos = tableEndPos + 1;
        tr.setSelection(TextSelection.near(tr.doc.resolve(firstCellPos)));
        view.dispatch(tr.scrollIntoView());
        view.focus();
        return true;
    }

    // 2. If parent paragraph is empty, replace it cleanly with table + trailing paragraph
    const parent = $from.parent;
    if (parent && parent.isTextblock && parent.content.size === 0 && $from.depth > 0) {
        const tr = state.tr;
        const from = $from.before();
        const to = $from.after();
        const nodesToInsert = trailingP ? [tableNode, trailingP] : [tableNode];
        tr.replaceWith(from, to, nodesToInsert);
        const firstCellPos = from + 1;
        tr.setSelection(TextSelection.near(tr.doc.resolve(firstCellPos)));
        view.dispatch(tr.scrollIntoView());
        view.focus();
        return true;
    }

    // 3. Try standard insertTable command first
    try {
        if (editor.can().insertTable({ rows: rowCount, cols: colCount, withHeaderRow })) {
            const ok = editor.chain().focus().insertTable({ rows: rowCount, cols: colCount, withHeaderRow }).run();
            if (ok) return true;
        }
    } catch (err) {
        console.warn('Standard insertTable failed, falling back to direct PM insert:', err);
    }

    // 4. ProseMirror direct insertion fallback
    try {
        const tr = state.tr;
        let insertPos = selection.to;
        if (selection.node) {
            insertPos = selection.to;
        }
        insertPos = Math.max(0, Math.min(tr.doc.content.size, insertPos));
        const nodesToInsert = trailingP ? [tableNode, trailingP] : [tableNode];
        tr.insert(insertPos, nodesToInsert);
        const firstCellPos = insertPos + 1;
        tr.setSelection(TextSelection.near(tr.doc.resolve(firstCellPos)));
        view.dispatch(tr.scrollIntoView());
        view.focus();
        return true;
    } catch (err) {
        console.error('All table insertion attempts failed:', err);
        return false;
    }
}

export function TableGridSelector({ editor }) {
    const [isOpen, setIsOpen] = useState(false);
    const [hoveredRow, setHoveredRow] = useState(0);
    const [hoveredCol, setHoveredCol] = useState(0);
    const [customRows, setCustomRows] = useState(3);
    const [customCols, setCustomCols] = useState(3);
    const [withHeader, setWithHeader] = useState(true);
    const popoverRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (popoverRef.current && !popoverRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleInsert = (rows, cols, header = withHeader) => {
        safeInsertTable(editor, rows, cols, header);
        setIsOpen(false);
    };

    const GRID_ROWS = 8;
    const GRID_COLS = 8;

    return (
        <div className="table-grid-wrapper" ref={popoverRef} style={{ position: 'relative' }}>
            <button
                type="button"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setIsOpen(!isOpen)}
                className={`toolbar-btn ${editor?.isActive('table') ? 'is-active' : ''}`}
                title="Insert Table"
                aria-label="Insert Table"
            >
                <TableIcon size={18} />
            </button>

            {isOpen && (
                <div className="table-grid-popover" style={{ zIndex: 100 }}>
                    <div className="table-grid-header">
                        <span style={{ fontWeight: 600 }}>
                            {hoveredRow > 0 && hoveredCol > 0
                                ? `${hoveredCol} × ${hoveredRow} Table`
                                : 'Insert Table'}
                        </span>
                    </div>

                    {/* Quick presets for immediate 1-click insertion */}
                    <div className="table-quick-presets" style={{ display: 'flex', gap: '4px', marginBottom: '10px' }}>
                        <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleInsert(3, 3, true)}
                            className="table-preset-btn highlight"
                            title="Insert standard 3 × 3 Table"
                        >
                            3 × 3 (Default)
                        </button>
                        <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleInsert(2, 2, true)}
                            className="table-preset-btn"
                            title="Insert 2 × 2 Table"
                        >
                            2 × 2
                        </button>
                        <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => handleInsert(4, 3, true)}
                            className="table-preset-btn"
                            title="Insert 4 × 3 Table"
                        >
                            4 × 3
                        </button>
                    </div>

                    {/* Interactive 8x8 Grid */}
                    <div
                        className="table-grid-cells"
                        onMouseLeave={() => {
                            setHoveredRow(0);
                            setHoveredCol(0);
                        }}
                    >
                        {Array.from({ length: GRID_ROWS }).map((_, rIndex) => (
                            <div key={rIndex} className="table-grid-row">
                                {Array.from({ length: GRID_COLS }).map((_, cIndex) => {
                                    const r = rIndex + 1;
                                    const c = cIndex + 1;
                                    const isSelected = r <= hoveredRow && c <= hoveredCol;
                                    return (
                                        <div
                                            key={cIndex}
                                            className={`table-grid-cell ${isSelected ? 'selected' : ''}`}
                                            onMouseEnter={() => {
                                                setHoveredRow(r);
                                                setHoveredCol(c);
                                            }}
                                            onMouseDown={(e) => e.preventDefault()}
                                            onClick={() => handleInsert(r, c, withHeader)}
                                            title={`${c} × ${r} Table`}
                                        />
                                    );
                                })}
                            </div>
                        ))}
                    </div>

                    <div style={{ fontSize: '11px', color: '#64748b', textAlign: 'center', margin: '6px 0 8px 0' }}>
                        {hoveredRow > 0 && hoveredCol > 0 ? (
                            <span style={{ color: 'var(--accent-teal, #068989)', fontWeight: 600 }}>
                                {hoveredCol} Columns × {hoveredRow} Rows (Click to insert)
                            </span>
                        ) : (
                            'Select grid or enter custom size below'
                        )}
                    </div>

                    <div style={{ height: '1px', background: '#e2e8f0', margin: '6px 0 8px 0' }} />

                    {/* Custom Size Form */}
                    <form
                        onSubmit={(e) => {
                            e.preventDefault();
                            handleInsert(customRows, customCols, withHeader);
                        }}
                        style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}
                    >
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                                <span style={{ color: '#475569' }}>Rows:</span>
                                <input
                                    type="number"
                                    min="1"
                                    max="50"
                                    value={customRows}
                                    onChange={(e) => setCustomRows(Math.max(1, parseInt(e.target.value) || 1))}
                                    style={{
                                        width: '46px',
                                        padding: '3px 6px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        textAlign: 'center',
                                    }}
                                />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '12px' }}>
                                <span style={{ color: '#475569' }}>Cols:</span>
                                <input
                                    type="number"
                                    min="1"
                                    max="20"
                                    value={customCols}
                                    onChange={(e) => setCustomCols(Math.max(1, parseInt(e.target.value) || 1))}
                                    style={{
                                        width: '46px',
                                        padding: '3px 6px',
                                        border: '1px solid #cbd5e1',
                                        borderRadius: '4px',
                                        fontSize: '12px',
                                        textAlign: 'center',
                                    }}
                                />
                            </div>
                        </div>

                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px' }}>
                            <label style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: '11.5px', color: '#64748b', cursor: 'pointer' }}>
                                <input
                                    type="checkbox"
                                    checked={withHeader}
                                    onChange={(e) => setWithHeader(e.target.checked)}
                                    style={{ accentColor: 'var(--accent-teal, #068989)' }}
                                />
                                Header row
                            </label>

                            <button
                                type="submit"
                                onMouseDown={(e) => e.preventDefault()}
                                className="btn-primary"
                                style={{
                                    padding: '4px 10px',
                                    fontSize: '11.5px',
                                    fontWeight: 600,
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                }}
                            >
                                <Plus size={13} />
                                Insert
                            </button>
                        </div>
                    </form>
                </div>
            )}
        </div>
    );
}
